import sizeSystem from '@/shared/size-system'
import {
  Camera,
  HalfFloatType,
  LinearEncoding,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget
} from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import CompositingPass from './shaders/compositing-pass'
import FXAAPass from './shaders/fxaa-pass'
//import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import UnrealBloomPass from './shaders/unreal-bloom-pass'
import zoomBlurPass from './shaders/zoom-blur-pass'

const blackMaterial = new MeshBasicMaterial({ color: 0x000000 })
const blackMaterialSkinned = new MeshBasicMaterial({ color: 0x000000 })

export default class PostEffectManager {
  #renderer: WebGLRenderer
  #scene: Scene = {} as Scene
  #camera: Camera = {} as Camera
  #bloomAmount: number
  #bloomProcessObjects: Object3D[] = []
  #renderTargetBloom: WebGLRenderTarget
  #renderTargetGeneric: WebGLRenderTarget
  #bloomComposer: EffectComposer
  #genericComposer: EffectComposer
  #bloomPass: UnrealBloomPass
  #fxaaPass: ShaderPass
  #zoomBlurPass: ShaderPass
  #compositingPass: ShaderPass
  #renderPass: RenderPass
  #particlesUseBloom = false
  #fadeAmount = 0

  constructor(renderer: WebGLRenderer) {
    this.#renderer = renderer
    this.#bloomAmount = 0
    // Create your composer and first RenderPass

    this.#renderTargetBloom = new WebGLRenderTarget(window.innerWidth, window.innerHeight, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
      stencilBuffer: true,
      encoding: LinearEncoding,
      type: HalfFloatType
    })
    this.#renderTargetGeneric = new WebGLRenderTarget(window.innerWidth, window.innerHeight, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
      stencilBuffer: true,
      encoding: LinearEncoding,
      type: HalfFloatType
    })

    this.#bloomComposer = new EffectComposer(this.#renderer, this.#renderTargetBloom)
    this.#bloomComposer.setPixelRatio(1)

    this.#genericComposer = new EffectComposer(this.#renderer, this.#renderTargetGeneric)
    this.#genericComposer.setPixelRatio(sizeSystem.size.sizeRatio)

    this.#bloomPass = new UnrealBloomPass(new Vector2(window.innerWidth, window.innerHeight), 0.61, 0.08, 0.06)
    this.#bloomPass.needsSwap = false
    this.#bloomPass.threshold = 0.06
    this.#bloomPass.strength = 0.31
    this.#bloomPass.radius = 0.06
    this.#bloomPass.enabled = true

    this.#zoomBlurPass = new ShaderPass(zoomBlurPass)
    this.#fxaaPass = new ShaderPass(FXAAPass)
    this.#fxaaPass.enabled = window.devicePixelRatio === 1

    this.#renderPass = new RenderPass(this.#scene, this.#camera, undefined, undefined, 1)
    this.#renderPass.clear = true
    this.#renderPass.needsSwap = false

    this.#bloomComposer.addPass(this.#renderPass)
    this.#bloomComposer.addPass(this.#bloomPass)

    this.#compositingPass = new ShaderPass(CompositingPass)
    this.#compositingPass.uniforms.tBloom.value = this.#bloomComposer.renderTarget2.texture
    this.#compositingPass.renderToScreen = false

    // final render stack
    this.#genericComposer.addPass(this.#renderPass)
    //this.#genericComposer.addPass(this.#fxaaPass)
    //this.#genericComposer.addPass(this.#zoomBlurPass)
    this.#genericComposer.addPass(this.#compositingPass)

    this.setBloomAmount(0.6)

    /*if (debugSystem.isDebug) {
      const tweakpaneObject = {
        bloomAmount: this.#bloomAmount,
        bloomStrength: this.#bloomPass.strength,
        bloomThreshold: this.#bloomPass.threshold,
        bloomRadius: this.#bloomPass.radius,
        particlesUseBloom: this.#particlesUseBloom,
        useFXAA: this.#fxaaPass.enabled,
        useBloom: this.#bloomAmount > 0
      }

      tweakpaneManager.pane
        .addInput(tweakpaneObject, 'particlesUseBloom', { label: 'Particles bloom' })
        .on('change', () => {
          this.#particlesUseBloom = tweakpaneObject.particlesUseBloom
        })

      tweakpaneManager.pane.addInput(tweakpaneObject, 'useFXAA', { label: 'Use FXAA' }).on('change', () => {
        this.#fxaaPass.enabled = tweakpaneObject.useFXAA
      })

      tweakpaneManager.pane.addInput(tweakpaneObject, 'useBloom', { label: 'Use Bloom' }).on('change', () => {
        this.#bloomAmount = tweakpaneObject.useBloom ? 0.8 : 0
      })

      tweakpaneManager.pane
        .addInput(tweakpaneObject, 'bloomAmount', { min: 0, max: 1, label: 'Bloom amount' })
        .on('change', () => {
          this.setBloomAmount(tweakpaneObject.bloomAmount)
        })

      tweakpaneManager.pane
        .addInput(tweakpaneObject, 'bloomStrength', { min: 0, max: 4, label: 'Bloom strength' })
        .on('change', () => {
          this.#bloomPass.strength = tweakpaneObject.bloomStrength
        })

      tweakpaneManager.pane
        .addInput(tweakpaneObject, 'bloomThreshold', { min: 0, max: 1, label: 'Bloom threshold' })
        .on('change', () => {
          this.#bloomPass.threshold = tweakpaneObject.bloomThreshold
        })

      tweakpaneManager.pane
        .addInput(tweakpaneObject, 'bloomRadius', { min: 0, max: 1, label: 'Bloom radius' })
        .on('change', () => {
          this.#bloomPass.radius = tweakpaneObject.bloomRadius
        })
    }*/
  }

  set fadeAmount(value: number) {
    this.#fadeAmount = value
    this.#compositingPass.uniforms.fadeAmount.value = this.#fadeAmount
  }

  setSceneAndCamera(scene: Scene, camera: Camera): void {
    this.#scene = scene
    this.#camera = camera

    this.#renderPass.camera = camera
    this.#renderPass.scene = scene
  }

  setBloomAmount(value: number): void {
    this.#bloomAmount = value
    this.#compositingPass.uniforms.bloomAmount.value = this.#bloomAmount
  }

  updateBloomList() {
    //collect all affected objects
    this.#bloomProcessObjects.length = 0

    if (this.#scene && this.#scene.children) {
      //TODO: Optimize: Filter out more objects, like childs of parents with hideFromBloom
      this.#scene.traverse((obj: any) => {
        if (
          (obj.type === 'Mesh' ||
            obj.type === 'SkinnedMesh' ||
            obj.type === 'Group' ||
            obj.userData.isParticleSystem) &&
          (obj.userData.enableBloom === true ||
            obj.userData.enableBloom === false ||
            obj.userData.hideFromBloom ||
            obj.userData.isParticleSystem)
        ) {
          if (
            !obj.name.includes('Collider') &&
            !obj.name.includes('Sensor') &&
            !this.#bloomProcessObjects.find(x => obj.uuid && x.uuid === obj.uuid)
          ) {
            this.#bloomProcessObjects.push(obj)
          }
        }
      })
    }
  }

  render(time: number, delta: number) {
    if (this.#bloomAmount > 0) {
      this.#bloomPass.enabled = true
      if (this.#bloomProcessObjects.length === 0) {
        this.updateBloomList()
      }

      this.#bloomProcessObjects.forEach((obj: Object3D) => {
        //hide object completelly, like camera black fade plane
        if (obj.userData.hideFromBloom) {
          obj.userData.visibleStateBeforeBloom = obj.visible
          obj.visible = false
        } else if (obj.userData.isParticleSystem) {
          if (!this.#particlesUseBloom) {
            obj.visible = false
          }
        }

        //hide object but contribute to depth for selective bloom
        else if (obj.userData.enableBloom === false || obj.userData.enableBloom === undefined) {
          const mesh = obj as Mesh
          mesh.userData.originalMaterial = mesh.material
          mesh.material = mesh.type === 'SkinnedMesh' ? blackMaterialSkinned : blackMaterial
          //if (mesh.material) {
          //  ;(mesh.material as Material).colorWrite = false
          // }
        } else {
          const mesh = obj as Mesh
          if (mesh) {
            const mat = mesh.material as ShaderMaterial
            if (mat.userData.shader && mat.userData.shader.uniforms.isBloomRender) {
              mat.userData.shader.uniforms.isBloomRender.value = 1
            }

            if (mat.uniforms && mat.uniforms.isBloomRender) {
              mat.uniforms.isBloomRender.value = 1
            }
          }
        }
      })

      this.#bloomComposer.render(delta)

      this.#bloomProcessObjects.forEach((obj: Object3D) => {
        if (obj.userData.hideFromBloom) {
          obj.visible = obj.userData.visibleStateBeforeBloom
        } else if (obj.userData.isParticleSystem) {
          obj.visible = true
        } else if (obj.userData.enableBloom === false || obj.userData.enableBloom === undefined) {
          const mesh = obj as Mesh
          //if (mesh.material) {
          //  ;(mesh.material as Material).colorWrite = true
          //}
          mesh.material = mesh.userData.originalMaterial
        } else {
          const mesh = obj as Mesh
          if (mesh) {
            const mat = mesh.material as ShaderMaterial
            if (mat.userData.shader && mat.userData.shader.uniforms.isBloomRender) {
              mat.userData.shader.uniforms.isBloomRender.value = 0
            }

            if (mat.uniforms && mat.uniforms.isBloomRender) {
              mat.uniforms.isBloomRender.value = 0
            }
          }
        }
      })
    } else {
      this.#bloomPass.enabled = false
    }

    this.#genericComposer.render(delta)

    this.#renderer.setRenderTarget(null)
  }

  resize() {
    const size = sizeSystem.size
    const w = size.width // / size.sizeRatio
    const h = size.height // / size.sizeRatio

    this.#genericComposer.setSize(w, h)
    this.#bloomComposer.setSize(w, h)

    this.#fxaaPass.uniforms.resolution.value.set(w, h)
    this.#compositingPass.uniforms.resolution.value.set(w, h)
    this.#compositingPass.uniforms.tBloom.value = this.#bloomComposer.renderTarget2.texture
  }

  dispose() {
    /*this.#renderer = undefined
    this.#scene = undefined
    this.#camera = undefined*/
  }
}
